import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Check, ChevronDown, Search, X } from "lucide-react";
import styles from "./SearchableSelect.module.scss";

/* ────────────────────────────────────────────────────────────
 * SearchableSelect — single-choice dropdown with a built-in
 * search input. Mirrors the MultiSelect primitive's structure
 * (popup, search bar, option list, footer) so the two feel like
 * siblings in the design system.
 *
 *   <SearchableSelect
 *     label="Employee"
 *     placeholder="Select Employee"
 *     value={selectedId}
 *     onChange={setSelectedId}
 *     options={[
 *       { value: 12, label: "Numaan Kazi", sublabel: "Engineering" },
 *       …
 *     ]}
 *   />
 *
 * value is a single primitive (string | number | null), matching
 * the shape callers already use against native `<select>`.
 * ──────────────────────────────────────────────────────────── */

const SearchableSelect = ({
  label,
  placeholder,
  value,
  onChange,
  options,
  searchValue,
  onSearchChange,
  loading,
  emptyMessage,
  loadingMessage,
  disabled,
  clearable,
  error,
  helperText,
  id,
  className,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState("");
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchInputRef = useRef(null);

  // Popup is rendered into a portal at <body> so it escapes any ancestor
  // `overflow: hidden` (e.g. PagePanel's clip rule) and any stacking
  // context that would otherwise put it behind sticky table headers. The
  // position is computed from the trigger's bounding rect and kept in
  // sync on scroll/resize while the popup is open.
  const [popupRect, setPopupRect] = useState(null);

  const updatePopupRect = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    setPopupRect({
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
    });
  };

  const isControlledSearch = typeof searchValue === "string";
  const search = isControlledSearch ? searchValue : internalSearch;

  const setSearch = (q) => {
    if (!isControlledSearch) setInternalSearch(q);
    onSearchChange?.(q);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Trigger lives in the wrapper; the popup is portaled to <body>.
      // Either container hosting the click is "inside" — anything else
      // closes the menu.
      const inTrigger =
        wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Lock the popup to the trigger's current rect while it's open. Run
  // synchronously after layout (useLayoutEffect) on open so the very
  // first paint has the popup correctly placed, then attach passive
  // scroll/resize listeners on the window AND on every scrollable
  // ancestor (a PagePanel inside a sticky-table region scrolls
  // internally, and the popup needs to follow that, not just window
  // scroll).
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopupRect();
    const scrollParents = [];
    let node = triggerRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollParents.push(node);
      }
      node = node.parentElement;
    }
    const handler = () => updatePopupRect();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    scrollParents.forEach((n) => n.addEventListener("scroll", handler));
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      scrollParents.forEach((n) => n.removeEventListener("scroll", handler));
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Reset internal search when the popup closes so reopening the menu
  // doesn't surprise the user with a stale filter.
  useEffect(() => {
    if (!open && !isControlledSearch) setInternalSearch("");
  }, [open, isControlledSearch]);

  const selectedOption = useMemo(() => {
    if (value == null || value === "") return null;
    return (
      options.find((o) => String(o.value) === String(value)) || {
        value,
        label: String(value),
      }
    );
  }, [value, options]);

  const filteredOptions = useMemo(() => {
    if (isControlledSearch) return options;
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label || ""} ${o.sublabel || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search, isControlledSearch]);

  const select = (val,o) => {
    onChange?.(val,o);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const triggerClasses = [
    styles.trigger,
    open ? styles.triggerOpen : "",
    error ? styles.triggerError : "",
    disabled ? styles.triggerDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [styles.wrapper, className || ""]
    .filter(Boolean)
    .join(" ");

  const showClear = clearable && !disabled && selectedOption;

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        className={triggerClasses}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={styles.value}>
          {selectedOption ? (
            <span className={styles.valueLabel}>{selectedOption.label}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </span>

        {showClear && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear selection"
            onClick={clear}
          >
            <X size={14} />
          </button>
        )}

        <span
          className={[styles.caret, open ? styles.caretOpen : ""]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <ChevronDown size={16} />
        </span>
      </div>

      {open && popupRect &&
        createPortal(
          <div
            ref={popupRef}
            className={styles.popup}
            role="listbox"
            style={{
              position: "fixed",
              top: popupRect.top,
              left: popupRect.left,
              width: popupRect.width,
            }}
          >
            <div className={styles.searchBar}>
            <span className={styles.searchIcon}>
              <Search size={14} />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div className={styles.loadingRow}>{loadingMessage}</div>
          ) : filteredOptions.length === 0 ? (
            <div className={styles.empty}>{emptyMessage}</div>
          ) : (
            <ul className={styles.list}>
              {filteredOptions.map((o) => {
                const checked =
                  selectedOption &&
                  String(selectedOption.value) === String(o.value);
                const cls = [
                  styles.option,
                  checked ? styles.optionSelected : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <li
                    key={o.value}
                    role="option"
                    aria-selected={checked}
                    className={cls}
                    onClick={() => select(o.value,o)}
                  >
                    <span className={styles.optionBody}>
                      <div className={styles.optionLabel}>{o.label}</div>
                      {o.sublabel && (
                        <div className={styles.optionSub}>{o.sublabel}</div>
                      )}
                    </span>
                    {checked && (
                      <span className={styles.optionCheck} aria-hidden="true">
                        <Check size={14} strokeWidth={3} />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          </div>,
          document.body
        )}

      {error && <span className={styles.error}>{error}</span>}
      {!error && helperText && (
        <span className={styles.helperText}>{helperText}</span>
      )}
    </div>
  );
};

SearchableSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      sublabel: PropTypes.string,
    })
  ),
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

SearchableSelect.defaultProps = {
  label: "",
  placeholder: "Select…",
  value: null,
  options: [],
  searchValue: undefined,
  onSearchChange: undefined,
  loading: false,
  emptyMessage: "No options found",
  loadingMessage: "Loading…",
  disabled: false,
  clearable: false,
  error: "",
  helperText: "",
  id: undefined,
  className: "",
  ariaLabel: undefined,
};

export default SearchableSelect;
